import React from 'react';
import theme from '../Admin.scss';
import {getMessage as msg} from 'MessageBundle'
import BaseComponent from 'common/BaseComponent';
import Table from 'common/Table';
import Select from 'common/Select';
import deleteIcon from './delete.png';
import newItemIcon from './newItem.png';
import update from 'immutability-helper';
import conf from 'configuration.json';
import {ValidationResult, validate, required, generateStrategyResult, focusFirstError} from 'validation';
import ErrorBox from 'common/ErrorBox';
import ProgressBar from 'common/ProgressBar';
import Input from 'common/Input';

export default class ParameterTab extends BaseComponent {

    constructor(props) {
        super(props);

        this.parameterTypes = new Map([
            ['complaintTypes', msg("admin.parameterTab.complaintType")],
            ['complaintReasons', msg("admin.parameterTab.complaintReason")],
            ['complaintCauses', msg("admin.parameterTab.complaintCause")],
            ['complaintProducts', msg("admin.parameterTab.complaintProduct")]
        ]);

        this.state = {
            type: "",
            newItem: {name: "", active: false, deletable: true},
            dataChanged: false,
            processing: false
        };

        this.tableStructure = [
            {
                attribute: "value", label: msg("admin.parameterTab.value"), title: msg("admin.parameterTab.value.title"), width: '790px',
                render: (row) => {
                    return <span>{row.name}</span>;
                }
            },

            {
                attribute: "status", label: msg("admin.parameterTab.status"), title: msg("admin.parameterTab.value.status"), width: '80px',
                headStyle: theme.columnCheck, bodyStyle: theme.txtCenter,
                render: (row, index) => {
                    return (
                        <input type="checkbox" checked={row.active} onChange={this.handleItemStatusChange(index)}/>
                    );
                }
            },
            {
                attribute: "action", label: msg("admin.parameterTab.columnAction"), width: '80px', headStyle: theme.columnAction, bodyStyle: theme.txtCenter,
                render: (row, index) => {
                    return row.deletable ? (
                        <a href="javascript:void(0)" onClick={() => this.deleteParameter(index)} title={msg("admin.parameterTab.deleteItem")}>
                            <img src={deleteIcon} alt=""/>
                        </a>
                    ) : null;
                }
            }
        ];

        this.lastRowStructure = [
            {
                attribute: "value", label: msg("admin.parameterTab.value"), width: '790px', title: msg("admin.parameterTab.value.title"),
                render: () => {
                    return <Input id="newItem-id" name="newItem" value={this.state.newItem.name} onChange={this.handleItemNameChange}
                        errorMessages={this.state.errorMessages} maxLength={30} showExclamationMark={false}/>;
                }
            },
            {
                attribute: "status", label: msg("admin.parameterTab.status"), width: '80px', title: msg("admin.parameterTab.value.status"),
                headStyle: theme.columnCheck, bodyStyle: theme.txtCenter,
                render: () => {
                    return (
                        <input type="checkbox" checked={this.state.newItem.active} onChange={this.handleItemStatusChange(undefined)}/>
                    );
                }
            },
            {
                attribute: "action", label: msg("admin.parameterTab.columnAction"), width: '80px', headStyle: theme.columnAction, bodyStyle: theme.txtCenter,
                render: () => {
                     return this.state.type ? (
                            <a href="javascript:void(0)" onClick={this.addNewParameter} title={msg("admin.parameterTab.newItem")}>
                                <img src={newItemIcon} alt=""/>
                            </a>
                        )
                        : <a href="javascript:void(0)" title={msg("admin.parameterTab.newItem")}>
                             <img src={newItemIcon} alt=""/>
                         </a>
                }
            }
        ];

        const checkDuplicate = () => {
            const errors = [];
            if (this.state.parameters[this.state.type].map(a => a.name).includes(this.state.newItem.name)) {
                errors.push(msg('admin.parameterTab.duplicateValueError'));
            }
            return generateStrategyResult(errors)
        };

        this.validation = {
            common: [
                {
                    properties: ["state.newItem.name"],
                    individualNames: ["newItem"],
                    strategies: [
                        required(msg("admin.parameterTab.nameRequiredError")),
                        checkDuplicate
                    ]
                }
            ]
        };

        this.elementOrder = {
            newItem : "newItem-id"
        };
    }

    componentDidMount() {
        this.reloadData();
    }

    reloadData() {
        this.setState({processing: true});
        this.request('get', conf.rest.parameterEndpoint).send((result) => {
            this.sortParametersByName(result.body);
            this.setState({
                parameters: result.body,
                processing: false,
                dataChanged: false,
                newItem: {name: "", active: false, deletable: true},
                errorMessages: []
            });
        });
    }

    handleParameterTypeChange = (e) => {
        this.setState({
            type: e.target.value
        });
    };

    handleItemNameChange = (e) => {
        const changedItem = update(this.state.newItem, {name: {$set: e.target.value}});
        this.handleChange("newItem")(changedItem);
    };

    handleResetButtonClick = () => {
        if (!this.state.dataChanged || confirm(msg("admin.confirmResetChanges"))) {
            this.reloadData();
        }
    };

    handleItemStatusChange = (index) => (e) => {
        if(index !== undefined) {
            const changedType = update(this.state.parameters[this.state.type], {[index]: {active: {$set: e.target.checked}}});
            this.setState({
                parameters: update(this.state.parameters, {
                    $merge: {
                        [this.state.type]: changedType
                    }
                }),
                dataChanged: true
            })
        }
        else {
            this.handleNestedChange("newItem", "active")(e);
        }
    };

    addNewParameter = () => {
        const validationResult = validate(this, this.validation, ["common"]);
        if (validationResult.isEmpty()) {
            this.setState({
                errorMessages: {}
            });
            const changedType = update(this.state.parameters[this.state.type], {$push: [this.state.newItem]});
            this.setState({
                parameters: update(this.state.parameters, {
                    $merge: {
                        [this.state.type]: changedType
                    }
                }),
                newItem: {name: "", active: false, deletable: true},
                dataChanged: true
            })
        }
        else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    deleteParameter = (index) => {
        if(confirm(msg("admin.parameterTab.delete.confirmation"))) {
            const changedType = update(this.state.parameters[this.state.type], {$splice: [[index, 1]]});
            this.setState({
                parameters: update(this.state.parameters, {
                    $merge: {
                        [this.state.type]: changedType
                    }
                }),
                dataChanged: true
            })
        }
    };

    handleSave = () => {
        this.setState({processing: true});
        this.request('post', conf.rest.parameterEndpoint).params(this.state.parameters).send((result) => {
            this.sortParametersByName(result.body);
            this.setState({
                parameters: result.body,
                dataChanged: false,
                processing: false,
                errorsMessage: []
            });
        }, () => {
            const validationResult = new ValidationResult(this.state.errorMessages);
            validationResult.setError("save", msg("common.technicalError"));
            this.setState({
                errorMessages: validationResult.getErrorMessages(),
                processing: false
            });
        });
    };

    sortParametersByName(parameters) {
        Object.keys(parameters).map((e) => {parameters[e].sort((a, b) => a.name.localeCompare(b.name))});
    }

    render() {
        return (
            <div className={theme.parameterTab}>
                <ErrorBox groups={["common", "save"]} errorMessages={this.state.errorMessages}/>
                <Select id="typificationCategory-select" label={msg("admin.parameterTab.typificationCategorySelect")}
                    value={this.state.type} options={this.parameterTypes} onChange={this.handleParameterTypeChange} className={theme.row + ' ' + theme.rowCategorySelect}/>

                <div className={theme.tblWrap}>
                    <div className={theme.tblWrapInner}>
                        <Table structure={this.tableStructure} lastRowStructure={this.lastRowStructure}
                            value={this.state.type ? this.state.parameters[this.state.type] : []}
                            className={theme.parameterTabTbl}/>
                    </div>
                </div>

                <div className={theme.btnGroupMini + " " + theme.btnAdmin}>
                    <span>{msg("admin.parameterTab.changeBtnsInfo")}</span>
                    <button onClick={this.handleResetButtonClick}>{msg("admin.parameterTab.skipChangesBtn")}</button>
                    <button onClick={this.handleSave} disabled={!this.state.dataChanged} className={theme.buttonAccent}>
                        {msg("admin.parameterTab.saveChangesBtn")}
                    </button>
                </div>
                <ProgressBar hidden={!this.state.processing} />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/dialogs/VPRekla/Admin/ParameterTab/index.js